import type { UiVoll } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { schluessel, useUi } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { useDarf } from "../huelle/konto.ts";
import { allesGespeichert } from "../live/live.ts";
import { Bestaetigung } from "../ui/Dialog.tsx";
import { useHinweis } from "../ui/Hinweise.tsx";
import { befundZahlen, useBefunde } from "./befunde.ts";
import "./planung.css";

function FreigabeDialog({ ui, offen, onSchliessen }: { ui: UiVoll; offen: boolean; onSchliessen: () => void }) {
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const befunde = useBefunde(ui);
  const z = befundZahlen(befunde);
  const nummer = (ui.letzterStand ?? 0) + 1;

  const freigeben = async () => {
    if (!(await allesGespeichert())) {
      hinweis({ text: "Noch nicht alle Änderungen sind gespeichert; die Freigabe wurde nicht ausgeführt.", art: "fehler" });
      return;
    }
    try {
      const { stand } = await api<{ stand: number }>(`/api/uis/${ui.id}/freigeben`, { methode: "POST" });
      onSchliessen();
      await Promise.all([qc.invalidateQueries({ queryKey: schluessel.ui(ui.id) }), qc.invalidateQueries({ queryKey: schluessel.struktur }), qc.invalidateQueries({ queryKey: schluessel.staende(ui.id) })]);
      hinweis({ text: `Stand ${stand} ist freigegeben.` });
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
    }
  };

  return (
    <Bestaetigung offen={offen} titel="UI freigeben" bestaetigen={`Stand ${nummer} freigeben`} deaktiviert={z.fehler > 0} onBestaetigen={() => void freigeben()} onAbbrechen={onSchliessen}>
      <p>
        Friert den aktuellen Inhalt als <strong>Stand {nummer}</strong> ein. Claude baut ihn danach ins Plugin ein und meldet ihn als „Im Spiel“.
      </p>
      <p className={`freigabe-urteil${z.fehler ? " freigabe-urteil-fehler" : ""}`}>
        {z.fehler ? <CircleAlert size={15} aria-hidden="true" /> : <CircleCheck size={15} aria-hidden="true" />}
        {z.text}
        {z.fehler > 0 && " – erst beheben, dann freigeben."}
      </p>
      {befunde.length > 0 && (
        <ul className="befunde befunde-dialog">
          {befunde.map((b, i) => (
            <li key={i} className="befund">
              {b.stufe === "fehler" ? <CircleAlert size={14} className="befund-fehler" aria-label="Fehler" /> : <TriangleAlert size={14} className="befund-warnung" aria-label="Warnung" />}
              <span>
                <span className="befund-ort">{b.ort}</span> {b.meldung}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Bestaetigung>
  );
}

export function FreigebenKnopf({ uiId }: { uiId: string }) {
  const darf = useDarf("freigeben");
  const ui = useUi(uiId);
  const [offen, setOffen] = useState(false);
  if (!darf || !ui.data) return null;
  const nichtsNeu = ui.data.letzterStand !== null && !ui.data.geaendertSeitStand;
  return (
    <>
      <button
        type="button"
        className="knopf knopf-primaer"
        title={nichtsNeu ? `Nichts Neues seit Stand ${ui.data.letzterStand}` : "Freigeben"}
        disabled={nichtsNeu}
        onClick={() => setOffen(true)}
      >
        Freigeben
      </button>
      <FreigabeDialog ui={ui.data} offen={offen} onSchliessen={() => setOffen(false)} />
    </>
  );
}

/** Zeigt, welcher Stand freigegeben oder im Spiel ist und ob die UI seitdem geändert wurde. */
export function StandAnzeige({ uiId, onKlick }: { uiId: string; onKlick: () => void }) {
  const ui = useUi(uiId).data;
  if (!ui || ui.letzterStand === null) return null;
  const teile: string[] = [];
  if (ui.letzterStand !== ui.imSpielStand) teile.push(`Stand ${ui.letzterStand}`);
  if (ui.imSpielStand !== null) teile.push(`Im Spiel: Stand ${ui.imSpielStand}`);
  if (ui.geaendertSeitStand) teile.push("seitdem geändert");
  return (
    <button type="button" className={`stand-anzeige${ui.geaendertSeitStand ? " stand-geaendert" : ""}`} onClick={onKlick} title="Stände und Verlauf zeigen">
      {teile.join(" · ")}
    </button>
  );
}
